/**
 * Facturation (Billing) Types
 */

// Invoice status (defined first as it's used by other types)
export type InvoiceStatus = 'DRAFT' | 'GENERATED' | 'SENT' | 'PAID' | 'CANCELLED';

// Invoice operations that require status validation
export type InvoiceOperation = 'cancel' | 'validate' | 'finalize' | 'markPaid' | 'resyncFalco';

// Allowed statuses for each operation
export const ALLOWED_STATUSES_FOR_OPERATION: Record<InvoiceOperation, InvoiceStatus[]> = {
  cancel: ['DRAFT', 'GENERATED'],
  validate: ['DRAFT', 'GENERATED'],  // DRAFT (new flow) or GENERATED (legacy flow)
  finalize: ['DRAFT'],
  markPaid: ['GENERATED', 'SENT'],  // Cannot mark CANCELLED or already PAID as paid
  resyncFalco: ['SENT'],  // Can only resync invoices that have been sent
};

// Error messages for status validation
export const STATUS_ERROR_MESSAGES: Record<InvoiceOperation, string> = {
  cancel: 'Cannot cancel invoice. Only DRAFT or GENERATED invoices can be cancelled.',
  validate: 'Cannot validate invoice. Only DRAFT or GENERATED invoices can be validated.',
  finalize: 'Cannot finalize invoice. Only DRAFT invoices can be finalized.',
  markPaid: 'Cannot mark as paid. Only GENERATED or SENT invoices can be marked as paid.',
  resyncFalco: 'Cannot resync with Falco. Only SENT invoices can be resynced.',
};

/**
 * Validates if an invoice operation is allowed based on current status
 * @param status Current invoice status
 * @param operation Operation to perform
 * @returns Object with valid boolean and optional error message
 */
export function validateInvoiceOperation(
  status: InvoiceStatus,
  operation: InvoiceOperation
): { valid: boolean; error?: string } {
  const allowedStatuses = ALLOWED_STATUSES_FOR_OPERATION[operation];
  if (allowedStatuses.includes(status)) {
    return { valid: true };
  }
  return {
    valid: false,
    error: STATUS_ERROR_MESSAGES[operation] + ` Current status: ${status}`,
  };
}

// Company bank account for invoice generation
export interface BankAccount {
  id: number;
  label: string;
  iban: string;
  bic: string;
  bank_name?: string;
  is_default: boolean;
}

// Product catalog item
export interface Product {
  id: number;
  product_code: string;
  description: string;
  unit_price_htva: number;
  tva_rate: number;
  is_active: boolean;
  honoraire_expert_htva?: number | null;  // Optional expert fee
  created_at: string;
  updated_at: string;
}

// =============================================================================
// TARIFS - Pricing Matrix (imported from Excel)
// =============================================================================

// Tarif - a single pricing entry (banque + service + prestation combination)
export interface Tarif {
  id: number;
  banque_code: string;      // 'PRIVE', 'BELFIUS', etc.
  banque_name: string;      // 'Privé', 'Belfius', etc.
  service_code: string;     // 'FULL_EXPERTISE', 'DRIVE_BY', 'DESKTOP', 'REOUVERTURE', 'REVISION'
  service_name: string;     // 'Full Expertise', 'Drive-By', etc.
  prestation_code: string;  // 'PRIVE-0001', 'BELFIUS-0001', etc.
  prestation_name: string;  // 'TERRAIN>150.000 €', etc.
  price_htva: number;       // Office fee (H Bureaux)
  price_tvac: number;       // Calculated: price_htva * 1.21
  honoraire_expert_htva: number | null; // Expert fee (internal tracking)
  tva_rate: number;
  sort_order: number;
  is_active: boolean;
  import_batch_id?: string | null;
  available_provinces?: string[]; // BRU, BW, HT, LG, LX, NA, AN, BF, FO, FOR, LI
  created_at: string;
  updated_at: string;
}

// Bank-level province defaults (the "RÈGLE BANQUE" Excel row)
export interface BanqueProvinces {
  banque_code: string;
  banque_name: string;
  available_provinces?: string[];
  updated_at: string;
}

// Distinct banque from tarifs
export interface TarifBanque {
  banque_code: string;
  banque_name: string;
  sort_order: number;
}

// Distinct service from tarifs
export interface TarifService {
  service_code: string;
  service_name: string;
}

// Prestation with pricing (for selection dropdown)
export interface TarifPrestation {
  id: number;
  prestation_code: string;
  prestation_name: string;
  price_htva: number;
  price_tvac: number;
  honoraire_expert_htva: number | null;
  tva_rate: number;
  sort_order: number;
  available_provinces?: string[];
}

// Filters for tarif queries
export interface TarifFilters {
  banqueCode?: string;
  serviceCode?: string;
  activeOnly?: boolean;
}

// Affaire context - remembered banque/service for subsequent product additions
export interface AffaireContext {
  banque_code: string;
  service_code: string;
}

// Import batch info
export interface TarifImportBatch {
  import_batch_id: string;
  tarif_count: number;
  imported_at: string;
  banques: string[];
}

// Import result
export interface TarifImportResult {
  batchId: string;
  totalImported: number;
  errorCount: number;
  errors: Array<{
    sheet: string;
    prestation: string;
    service: string;
    error: string;
  }>;
}

// Invoice detail for a product (which invoices paid for it)
export interface ProductInvoiceDetail {
  invoice_number: string;
  status: InvoiceStatus;
  quantity: number;
  amount_htva: number;
  // Optional fields populated by the enriched getAffaireProducts query
  invoice_id?: number;
  invoice_type?: 'SINGLE' | 'MONTHLY' | 'CUSTOM';
  invoice_date?: string | null;
  billing_name?: string | null;
}

// Payer track derived from the product's tarif (or fallback banque_code)
export type PayerTrack = 'CUSTOMER' | 'BANK_GROUPED';

// Monthly billing track attached to an affaire (returned by
// GET /api/billing/affaires/:id/monthly-billing-tracks)
export interface AffaireMonthlyTrack {
  billing_contact_id: string;
  billing_contact_name: string | null;
  billing_contact_vat: string | null;
  affaire_status: string | null;
  status_eligible: boolean;
}

// Reduction type
export type ReductionType = 'percentage' | 'fixed';

// Product assigned to an affaire
export interface AffaireProduct {
  id: number;
  uuid: string;  // Unique identifier for precise invoice tracking
  affaire_id: string;
  product_id: number | null;
  product_code: string;
  description: string;
  quantity: number;
  unit_price_htva: number;
  unit_price_tvac?: number;  // TVAC price - source of truth for exact calculations
  tva_rate: number;
  // Tarif reference - links to pricing matrix
  tarif_id?: number | null;
  banque_code?: string | null;  // Remembered for subsequent product additions
  service_code?: string | null; // Remembered for subsequent product additions
  // Partial billing tracking (0-100%), defaults to 0
  billed_percentage?: number;
  // Reduction fields
  reduction_type?: ReductionType | null;
  reduction_value?: number | null;
  reduction_code?: string | null;  // Reference code for the reduction
  // Expert fee (internal tracking only, not shown on invoice)
  honoraire_expert_htva?: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Calculated fields
  line_total_htva?: number;
  line_total_tva?: number;
  line_total_tvac?: number;
  // Calculated reduction amount
  reduction_amount_htva?: number;
  net_unit_price_htva?: number;  // unit_price_htva - reduction
  // Joined fields (can be null from LEFT JOIN)
  first_name?: string | null;
  last_name?: string | null;
  // Invoiced amounts (enriched by eligibility check)
  original_quantity?: number;
  original_htva?: number;
  invoiced_quantity?: number;
  invoiced_htva?: number;
  remaining_quantity?: number;
  remaining_htva?: number;
  invoiced_percentage?: number;
  fully_invoiced?: boolean;
  // Invoice details (which invoices paid for this product)
  invoices?: ProductInvoiceDetail[];
  // Mixed-payer enrichment (from the enriched getAffaireProducts query)
  payer_track?: PayerTrack;
  tarif_banque_code?: string | null;
  tarif_banque_name?: string | null;
  tarif_service_code?: string | null;
  tarif_service_name?: string | null;
  // Recomputed from invoice/credit-note lines on every fetch — prefer this
  // over the persisted `billed_percentage` until backfill has been run.
  computed_billed_percentage?: number;
  credited_quantity?: number;
  credited_htva?: number;
  // True when this product has at least one line on a DRAFT MONTHLY invoice
  queued_in_monthly_draft?: boolean;
}

// Product selection for invoice generation (with custom quantity or percentage)
export interface ProductSelection {
  productId: number;
  quantity: number;           // Legacy: quantity-based selection
  percentage?: number;        // New: percentage-based selection (1-100%)
}

// Product selection for percentage-based partial billing
export interface ProductPercentageSelection {
  productId: number;          // Affaire product ID
  percentage: number;         // Percentage to bill (1-100, 1% granularity)
  remainingPercentage?: number; // Available percentage (100 - billed_percentage)
}

// Invoice type
export type InvoiceType = 'SINGLE' | 'MONTHLY' | 'CUSTOM';

// Falco sync status
export type FalcoStatus = 'PENDING' | 'UPLOADED' | 'PROCESSING' | 'SENT' | 'FAILED';

// Peppol delivery status
export type PeppolStatus = 'PENDING' | 'DELIVERED' | 'FAILED' | 'NOT_REGISTERED';

// Invoice line item
export interface InvoiceLine {
  id: number;
  facture_id: number;
  affaire_id: string;
  affaire_product_uuid: string | null;  // Link to specific product allocation
  product_code: string | null;
  description: string;
  quantity: number;
  unit_price_htva: number;
  unit_price_tvac?: number;  // TVAC price - source of truth for exact calculations
  tva_rate: number;
  line_total_htva: number;
  line_total_tva: number;
  line_total_tvac: number;
  sort_order: number;
  created_at: string;
  // Credit note tracking
  credited_quantity?: number;  // How much quantity has been credited (legacy)
  credited_htva?: number;      // Amount already credited in HTVA (for HTVA-based tracking)
}

// Invoice
export interface Invoice {
  id: number;
  invoice_number: string | null;  // Null for DRAFT invoices (number assigned at validation)
  invoice_type: InvoiceType;
  status: InvoiceStatus;
  billing_contact_id: string | null;
  billing_name: string;
  billing_address: string | null;
  billing_vat_number: string | null;
  billing_email: string | null;
  total_htva: number;
  total_tva: number;
  total_tvac: number;
  invoice_date: string;
  due_date: string | null;
  pdf_path: string | null;
  pdf_generated_at: string | null;
  structured_communication: string | null;
  language: string;
  falco_document_id: string | null;
  falco_status: FalcoStatus | null;
  falco_synced_at: string | null;
  falco_error: string | null;
  falco_retry_count: number;
  peppol_status: PeppolStatus | null;
  peppol_delivery_id: string | null;
  peppol_delivered_at: string | null;
  peppol_error: string | null;
  // Email delivery status (optional - may not exist for older invoices)
  email_sent?: boolean;
  email_sent_at?: string | null;
  email_recipient?: string | null;
  email_error?: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Validation tracking
  validated_by?: string | null;
  validated_at?: string | null;
  // Joined fields (can be null from LEFT JOIN)
  first_name?: string | null;
  last_name?: string | null;
  validator_first_name?: string | null;
  validator_last_name?: string | null;
  lines?: InvoiceLine[];
  affaires?: InvoiceAffaire[];
  // Credit note tracking
  is_fully_credited?: boolean;  // True if all lines have been fully credited
  total_credited?: number;      // Total amount already credited (TVAC)
  // Test mode tracking
  is_test?: boolean;  // True for test invoices validated in test mode (not production)
  // Payment tracking (from LEFT JOIN with payment_affaire_mappings)
  total_payment_received?: number;  // Total amount received via linked payments
  payment_count?: number;           // Number of distinct payments linked
}

// Invoice-Affaire link
export interface InvoiceAffaire {
  affaire_id: string;
  k_id_dossier?: string | null;
  adresse_bien?: string | null;
}

// Credit note type
export type CreditNoteType = 'FULL' | 'PARTIAL' | 'CUSTOM';

// Credit note status
export type CreditNoteStatus = 'DRAFT' | 'GENERATED' | 'SENT' | 'CANCELLED';

// Credit note line
export interface CreditNoteLine {
  id: number;
  note_credit_id?: number;  // Not included in nested response
  original_ligne_id?: number | null;
  affaire_product_uuid?: string | null;  // Link to product allocation for tracking
  description: string;
  quantity: number;
  unit_price_htva: number;
  unit_price_tvac?: number;  // TVAC price - source of truth for exact calculations
  tva_rate: number;
  line_total_htva: number;
  line_total_tva: number;
  line_total_tvac: number;
  created_at?: string;  // Not included in nested response
}

// Credit note linked invoice (many-to-many)
export interface CreditNoteInvoice {
  id: number;
  note_credit_id: number;
  facture_id: number;
  invoice_number?: string | null;
  affaire_id?: string | null;
  k_id_dossier?: string | null;
}

// Credit note
export interface CreditNote {
  id: number;
  credit_note_number: string | null;  // Null for DRAFT credit notes (number assigned at validation)
  original_facture_id?: number | null; // null for custom credit notes (not linked to any invoice)
  credit_type: CreditNoteType;
  status: CreditNoteStatus;
  total_htva: number;
  total_tva: number;
  total_tvac: number;
  reason: string;
  pdf_path: string | null;
  pdf_generated_at: string | null;
  falco_document_id: string | null;
  falco_status: FalcoStatus | null;
  falco_synced_at: string | null;
  falco_error: string | null;
  peppol_status: PeppolStatus | null;
  peppol_delivery_id: string | null;
  peppol_delivered_at: string | null;
  peppol_error: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Validation tracking
  validated_by?: string | null;
  validated_at?: string | null;
  // Email tracking
  email_sent?: boolean;
  email_sent_at?: string | null;
  email_recipient?: string | null;
  email_error?: string | null;
  // Custom credit note fields
  is_custom?: boolean;
  title?: string | null;
  billing_email?: string | null;
  language?: string;
  // Joined fields (can be null from LEFT JOIN)
  original_invoice_number?: string | null;
  billing_name?: string | null;
  billing_address?: string | null;
  billing_vat_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  validator_first_name?: string | null;
  validator_last_name?: string | null;
  lines?: CreditNoteLine[];
  // Multiple linked invoices
  invoices?: CreditNoteInvoice[];
  // Test mode tracking
  is_test?: boolean;  // True for test credit notes finalized in test mode (not production)
  // Custom date (for credit notes created in a different year than the current one)
  credit_note_date?: string | null;
}

// VAT validation result
export interface VatValidation {
  success: boolean;
  cached?: boolean;
  validationId?: number;
  vatNumber: string;
  countryCode?: string;
  isValid: boolean;
  officialData?: {
    name: string | null;
    address: string | null;
    street: string | null;
    postalCode: string | null;
    city: string | null;
  };
  storedData?: {
    name: string | null;
    address: string | null;
  };
  comparison?: {
    nameMatches: boolean | null;
    addressMatches: boolean | null;
  };
  needsReview?: boolean;
  requestDate?: string;
  userAccepted?: boolean;
  error?: string;
  errorType?: string;
}

// Peppol registration
export interface PeppolRegistration {
  success: boolean;
  vatNumber: string;
  peppolRegistered: boolean;
  participantId: string | null;
  canReceiveInvoice: boolean;
  canReceiveCreditNote: boolean;
  checkedAt: string;
  cached?: boolean;
  error?: string;
}

// Billing contact for an affaire
export interface BillingContact {
  contact_id: string;
  name: string;
  address: string | null;
  street?: string | null;       // Street + number only (raw FM adresse_domicile_1)
  ville?: string | null;        // City (raw FM ville_domicile)
  code_postal?: string | null;  // Postal code (raw FM code_postal_domicile)
  vat_number: string | null;
  vat_formatted?: string | null;
  statut_juridique?: string | null;
  statut_tva?: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  pays?: string | null;  // Country code for VAT (e.g., "FR", "DE")
}

// Billing summary for an affaire
export interface BillingSummary {
  affaireId: string;
  lines: AffaireProduct[];
  totalHtva: number;
  totalTva: number;
  totalTvac: number;
  lineCount: number;
}

// Eligible affaire for monthly invoicing
export interface EligibleAffaire {
  affaire_id: string;
  k_id_dossier: string;
  adresse_bien: string | null;
  total_htva: number;
  total_tva: number;
  total_tvac: number;
  product_count?: number;
}

// Billing contact group for client-based invoice selection
export interface BillingContactGroup {
  billing_contact_id: string;
  billing_contact_name: string;
  billing_contact_role: string;
  billing_contact_vat?: string | null;     // VAT number for VIES validation
  billing_contact_address?: string | null; // Address for VIES validation
  billing_contact_email?: string | null;   // Email for invoice delivery
  billing_contact_pays?: string | null;    // Country code for VAT prefix (e.g., "LU", "FR")
  affaires: EligibleAffaire[];
  totals: {
    htva: number;
    tva: number;
    tvac: number;
  };
}

// Response from client-eligible endpoint
export interface ClientBillingEligibleResult {
  client_id: string;
  billing_contacts: BillingContactGroup[];
  total_affaires: number;
}

// Existing invoice summary (for eligibility check)
export interface ExistingInvoiceSummary {
  invoice_number: string;
  status: InvoiceStatus;
  total_tvac: number | string;
}

// Invoice eligibility check result
export interface EligibilityResult {
  eligible: boolean;
  reason?: string;
  affaire?: {
    id_affaire: string;
    stage_id: number;
    statut_affaire: string;
    k_id_dossier: string;
  };
  products?: AffaireProduct[];
  existingInvoices?: ExistingInvoiceSummary[];
  billingContact?: BillingContact | null;
}

// Paginated response
export interface PaginatedResponse<T> {
  success: boolean;
  data?: T[];
  invoices?: T[];
  creditNotes?: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Invoice sort columns
export type InvoiceSortBy = 'invoice_number' | 'billing_name' | 'invoice_type' | 'invoice_date' | 'total_tvac' | 'status' | 'affaire_id' | 'created_at';

// Invoice filters
export interface InvoiceFilters {
  status?: InvoiceStatus;
  type?: InvoiceType;
  search?: string;
  billingContactId?: string;  // Filter invoices by billing contact
  page?: number;
  limit?: number;
  sort_by?: InvoiceSortBy;
  sort_order?: 'ASC' | 'DESC';
}

// Credit note filters
export interface CreditNoteFilters {
  status?: CreditNoteStatus;
  search?: string;
  affaireId?: string;  // Filter credit notes by affaire
  page?: number;
  limit?: number;
}

// Form data for creating a product
export interface CreateProductData {
  productCode: string;
  description: string;
  unitPriceHtva: number;
  tvaRate?: number;
  isActive?: boolean;
  bankId?: number | null;  // null = all banks (universal), number = specific bank
  honoraireExpertHtva?: number | null;  // Optional expert fee (internal, not shown on invoice)
}

// Form data for adding product to affaire
export interface AddAffaireProductData {
  productId?: number;
  productCode?: string;
  description: string;
  quantity?: number;
  unitPriceHtva: number;
  unitPriceTvac?: number;  // TVAC price - source of truth to avoid rounding errors
  tvaRate?: number;
  // Reduction fields
  reductionType?: ReductionType | null;
  reductionValue?: number | null;
  reductionCode?: string | null;  // Reference code for the reduction
  // Expert fee (internal, not shown on invoice)
  honoraireExpertHtva?: number | null;
  // Tarif selection fields (3-step: banque → service → prestation)
  tarifId?: number;       // Selected tarif (prestation) from pricing matrix
  banqueCode?: string;    // Selected banque (remembered per affaire)
  serviceCode?: string;   // Selected service (remembered per affaire)
}

// Form data for creating credit note
export interface CreateCreditNoteData {
  invoiceIds: number[]; // Support multiple invoices
  creditType: CreditNoteType;
  reason: string;
  lines?: {
    originalLigneId?: number;
    invoiceId?: number; // Which invoice this line belongs to
    affaireProductUuid?: string; // Link to product allocation for tracking
    description: string;
    quantity: number;
    unitPriceHtva: number;
    unitPriceTvac?: number;  // TVAC price - source of truth for exact calculations
    tvaRate: number;
  }[];
}

// Discount code applied to invoice
export interface DiscountCode {
  code: string;
  type: 'percentage' | 'fixed';  // percentage = % off, fixed = fixed amount off
  value: number;                 // percentage (0-100) or fixed amount in EUR
  description?: string;          // Optional description for invoice
}

// VIES official data for professional customers
export interface ViesOfficialData {
  name?: string | null;
  address?: string | null;
}

// Form data for generating single invoice
export interface GenerateSingleInvoiceData {
  affaireId: string;
  language?: string;
  selectedProductIds?: number[];              // Legacy: If empty/undefined, use all products
  productSelections?: ProductSelection[];     // Products with custom quantities for partial invoicing
  productPercentages?: ProductPercentageSelection[]; // Percentage-based partial billing (1-100%)
  discount?: DiscountCode;                    // Optional discount
  viesOfficialData?: ViesOfficialData;        // VIES official data for professional customers
}

// Form data for generating monthly invoice
export interface GenerateMonthlyInvoiceData {
  affaireIds: string[];
  billingContactId: string;
  language?: string;
  discount?: DiscountCode;
  viesOfficialData?: ViesOfficialData;        // VIES official data for professional customers
}

// Sequence status
export interface SequenceStatus {
  year: number;
  invoices: number;
  creditNotes: number;
}

// Falco config status
export interface FalcoConfigStatus {
  configured: boolean;
  apiUrl: string;
  hasApiKey: boolean;
  hasAppSecret: boolean;
  companyInfo: {
    name: string;
    vatNumber: string;
  };
}

// =============================================================================
// DEVIS (QUOTES)
// =============================================================================

// Devis status
export type DevisStatus = 'DRAFT' | 'SENT';

// Devis line item
export interface DevisLine {
  id: number;
  devis_id: number;
  product_id: number | null;
  product_code: string | null;
  description: string;
  quantity: number;
  unit_price_htva: number;
  tva_rate: number;
  line_total_htva: number;
  line_total_tva: number;
  line_total_tvac: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Devis (Quote)
export interface Devis {
  id: number;
  devis_number: string;
  status: DevisStatus;
  // Contact info
  contact_id: string | null;
  contact_name: string;
  contact_address: string | null;
  contact_vat_number: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_custom_contact: boolean;
  // Amounts
  total_htva: number;
  total_tva: number;
  total_tvac: number;
  // Dates
  devis_date: string;
  validity_date: string | null;
  // PDF
  pdf_path: string | null;
  pdf_generated_at: string | null;
  // Conversion
  linked_affaire_id: string | null;
  converted_at: string | null;
  // Meta
  language: string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields (can be null from LEFT JOIN)
  first_name?: string | null;
  last_name?: string | null;
  lines?: DevisLine[];
}

// Contact search result
export interface ContactSearchResult {
  contact_id: string;
  name: string;
  address: string | null;
  // Split address parts (for forms with separate street/postal/city/country fields)
  address_street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  pays?: string | null;
  vat_number: string | null;
  email: string | null;
  phone: string | null;
}

// Devis filters
export interface DevisFilters {
  status?: DevisStatus;
  search?: string;
  page?: number;
  limit?: number;
}

// Form data for creating/updating devis
export interface CreateDevisData {
  contactId?: string;
  contactName: string;
  contactAddress?: string;
  contactVatNumber?: string;
  contactEmail?: string;
  contactPhone?: string;
  isCustomContact?: boolean;
  devisDate?: string;
  validityDate?: string;
  language?: string;
  notes?: string;
  lines?: AddDevisLineData[];
}

// Form data for adding/updating devis line
export interface AddDevisLineData {
  productId?: number;
  productCode?: string;
  description: string;
  quantity?: number;
  unitPriceHtva: number;
  tvaRate?: number;
}

// Form data for converting devis to affaire
export interface ConvertDevisData {
  affaireId: string;
}

// Paginated devis response
export interface DevisPaginatedResponse {
  success: boolean;
  devis: Devis[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// =============================================================================
// CUSTOM INVOICES - Invoices not linked to any affaire
// =============================================================================

// Belgian VAT rates (0% = exempt / hors champ, e.g. service to a non-EU customer)
export type BelgianVatRate = 0 | 6 | 12 | 21;

// Invoice languages
export type InvoiceLanguage = 'fr' | 'nl' | 'de' | 'en';

// Custom invoice customer (one-time, embedded in invoice)
export interface CustomInvoiceCustomer {
  name: string;         // Required
  address?: string;     // Street + number (UBL StreetName / Falco line1)
  postalCode?: string;  // Postal / ZIP code
  city?: string;        // City
  countryCode?: string; // 2-letter ISO country code (defaults to BE on the backend)
  email?: string;
  vatNumber?: string;
}

// Custom invoice line item
export interface CustomInvoiceLine {
  description: string;       // Required
  quantity: number;          // Required, > 0
  unitPriceHtva: number;     // Required
  tvaRate: BelgianVatRate;   // Required: 0, 6, 12, or 21
}

// Form data for creating custom invoice
export interface CreateCustomInvoiceData {
  title?: string;                     // Optional invoice title
  customer: CustomInvoiceCustomer;    // Customer info (embedded, one-time)
  language: InvoiceLanguage;          // Invoice language
  lines: CustomInvoiceLine[];         // Line items
  affaireId?: string;                 // Optional: link to affaire (for affaire-context invoicing)
  bankAccountId?: number;             // Optional: specific bank account for invoice
}
